import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Mic, Square, ArrowUp, Loader2, Check, Copy, Keyboard, AudioLines } from 'lucide-react';
import { VoiceEngine } from './voiceEngine';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICE_LABELS = {
  connecting: 'Connecting your microphone…',
  listening: 'Listening — go ahead, speak freely',
  hearing: 'Listening…',
  thinking: 'Thinking…',
  speaking: 'Speaking — start talking anytime to jump in',
  complete: 'All set — submit your brief, or share it with a teammate first',
  error: 'Voice unavailable — switched to typing',
};

export const OnboardChat = ({
  sessionId,
  shareToken,
  speakerName,
  initialMessages,
  initialCompleted,
  allowShare = true,
  initialMode = 'chat',
  onSubmitted,
}) => {
  const [mode, setMode] = useState(initialMode === 'voice' ? 'voice' : 'chat');
  const [voiceStatus, setVoiceStatus] = useState('connecting');
  const [messages, setMessages] = useState(initialMessages || []);
  const [draft, setDraft] = useState('');
  const [streamText, setStreamText] = useState(null);
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(!!initialCompleted);
  const [dictation, setDictation] = useState('idle'); // idle|recording|transcribing
  const [shareUrl, setShareUrl] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const engineRef = useRef(null);
  const recorderRef = useRef(null);
  const dictStreamRef = useRef(null);
  const bottomRef = useRef(null);
  const taRef = useRef(null);
  const completedRef = useRef(!!initialCompleted);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamText]);

  useEffect(() => () => {
    engineRef.current?.stop();
    dictStreamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const fetchTts = async (text) => {
    const res = await fetch(`${API}/onboard/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new Error('tts');
    const { audio_url } = await res.json();
    return audio_url;
  };

  /* Sends a message, streams the reply into the UI, returns { finalText, isComplete }. */
  const sendMessageCore = useCallback(async (text) => {
    const clean = text.trim();
    setError('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', speaker: speakerName, text: clean }]);
    setStreamText('');
    let full = '';
    let finalText = '';
    let isComplete = false;
    try {
      const res = await fetch(`${API}/onboard/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId || undefined,
          share_token: shareToken || undefined,
          text: clean,
          speaker_name: speakerName,
        }),
      });
      if (!res.ok) throw new Error('request failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.error) throw new Error(ev.error);
          if (ev.delta) {
            full += ev.delta;
            setStreamText(full.split('<<')[0]);
          }
          if (ev.done) {
            finalText = ev.text;
            isComplete = ev.completed;
          }
        }
      }
      if (!finalText) finalText = full.replace('<<COMPLETE>>', '').trim();
      setMessages((m) => [...m, { role: 'assistant', speaker: 'X Scale', text: finalText }]);
      setStreamText(null);
      if (isComplete) {
        setCompleted(true);
        completedRef.current = true;
      }
      return { finalText, isComplete };
    } catch (e) {
      setStreamText(null);
      setError('Something went wrong — please try again.');
      return { finalText: '', isComplete: false };
    } finally {
      setBusy(false);
    }
  }, [sessionId, shareToken, speakerName]);

  /* ---------- Voice conversation mode ---------- */
  const handleUtterance = useCallback(async (blob) => {
    const engine = engineRef.current;
    if (!engine) return;
    if (blob.size < 2000) { engine.startListening(); return; }
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'clip.webm');
      const res = await fetch(`${API}/onboard/stt`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('stt');
      const { text } = await res.json();
      if (!text) { engine.startListening(); return; }
      const { finalText, isComplete } = await sendMessageCore(text);
      if (!finalText) { engine.startListening(); return; }
      let audioUrl = null;
      try { audioUrl = await fetchTts(finalText); } catch {}
      if (audioUrl) await engine.speak(audioUrl, { resume: !isComplete });
      else if (!isComplete) engine.startListening();
      else setVoiceStatus('complete');
    } catch {
      engine.startListening();
    }
  }, [sendMessageCore]);

  const startVoiceMode = useCallback(async () => {
    setMode('voice');
    setVoiceStatus('connecting');
    const engine = new VoiceEngine({
      onState: setVoiceStatus,
      onUtterance: handleUtterance,
    });
    engineRef.current = engine;
    try {
      await engine.init();
      // If the specialist has an unheard opener (start of conversation), speak it first.
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant' && messages.length === 1) {
        setVoiceStatus('thinking');
        try {
          const url = await fetchTts(last.text);
          await engine.speak(url, { resume: !completedRef.current });
        } catch { engine.startListening(); }
      } else {
        engine.startListening();
      }
    } catch {
      engine.stop();
      engineRef.current = null;
      setMode('chat');
      setError('Microphone access denied — you can type instead.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUtterance]);

  const switchToChat = () => {
    engineRef.current?.stop();
    engineRef.current = null;
    setMode('chat');
    setError('');
  };

  useEffect(() => {
    if (initialMode === 'voice') startVoiceMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Chat mode dictation (mic fills the input box) ---------- */
  const toggleDictation = async () => {
    setError('');
    if (dictation === 'recording') {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      dictStreamRef.current = stream;
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 1500) { setDictation('idle'); return; }
        setDictation('transcribing');
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'clip.webm');
          const res = await fetch(`${API}/onboard/stt`, { method: 'POST', body: fd });
          if (!res.ok) throw new Error('stt');
          const { text } = await res.json();
          if (text) setDraft((d) => (d ? `${d} ${text}` : text));
        } catch {
          setError("Couldn't hear that — try again or type instead.");
        }
        setDictation('idle');
      };
      recorderRef.current = rec;
      rec.start();
      setDictation('recording');
    } catch {
      setError('Microphone access denied — you can type instead.');
      setDictation('idle');
    }
  };

  const handleSend = () => {
    if (!draft.trim() || busy) return;
    const text = draft;
    setDraft('');
    if (taRef.current) taRef.current.style.height = 'auto';
    sendMessageCore(text);
  };

  /* ---------- Share & submit ---------- */
  const handleShare = async () => {
    try {
      const res = await fetch(`${API}/onboard/${sessionId}/share`, { method: 'POST' });
      if (!res.ok) throw new Error('share');
      const { share_token } = await res.json();
      setShareUrl(`${window.location.origin}/onboard/shared/${share_token}`);
      setCopied(false);
      setShowShare(true);
    } catch {
      setError("Couldn't create a share link — please try again.");
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    engineRef.current?.stop();
    engineRef.current = null;
    try {
      const res = await fetch(`${API}/onboard/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId || undefined, share_token: shareToken || undefined }),
      });
      if (!res.ok) throw new Error('submit');
      onSubmitted();
    } catch {
      setError("Couldn't submit — please try again.");
      setSubmitting(false);
      if (mode === 'voice') startVoiceMode();
    }
  };

  const hint = () => {
    if (dictation === 'recording') return <>Listening — <b>tap the square when you're done</b>, your words will appear above</>;
    if (dictation === 'transcribing') return 'Transcribing…';
    if (busy) return 'Thinking…';
    if (completed) return <>All set — <b>submit</b> when you're ready, or share with a teammate first</>;
    return 'Type your answer, or tap the mic to dictate';
  };

  const canAct = !busy && !submitting && dictation === 'idle' && (mode !== 'voice' || ['listening', 'complete'].includes(voiceStatus));
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');

  const completeBanner = completed && !busy && (
    <div className="complete-banner" data-testid="complete-banner">
      <div className="cb-copy">
        <div className="cb-title">Your intake is complete.</div>
        <div className="cb-sub">Send it to the X Scale team — or share it with a teammate for their input first.</div>
      </div>
      <div className="cb-actions">
        {allowShare && (
          <button className="button button-secondary" onClick={handleShare} disabled={submitting} data-testid="complete-share-btn">
            Share with a teammate
          </button>
        )}
        <button className="button button-primary cb-submit" onClick={handleSubmit} disabled={submitting} data-testid="complete-submit-btn">
          {submitting ? 'Preparing brief…' : 'Submit project brief'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="onboard-chat" data-testid="onboard-chat">
      <div className="onboard-chat-head">
        <div className="section-label"><span /> PROJECT INTAKE</div>
        <div className="onboard-head-actions" style={completed && !busy ? { visibility: 'hidden' } : undefined}>
          {allowShare && (
            <button className="button button-secondary" onClick={handleShare} disabled={!canAct} data-testid="share-with-teammate-btn">
              Share with a teammate
            </button>
          )}
          <button className="button button-primary" onClick={handleSubmit} disabled={!canAct || messages.length < 3} data-testid="submit-brief-btn">
            {submitting ? 'Preparing brief…' : 'Submit project brief'}
          </button>
        </div>
      </div>

      {mode === 'voice' ? (
        <div className="voice-stage" data-testid="voice-stage">
          <div className={`voice-orb ${voiceStatus}`} data-testid="voice-orb">
            <div className="voice-orb-core" />
          </div>
          <div className="voice-status" data-testid="voice-status">{VOICE_LABELS[voiceStatus] || ''}</div>
          <div className="voice-caption" data-testid="voice-caption">
            {streamText !== null ? streamText : lastAssistant?.text}
          </div>
          {error && <div className="onboard-error" style={{ textAlign: 'center' }}>{error}</div>}
          {completeBanner}
          <button className="button button-secondary voice-switch" onClick={switchToChat} data-testid="switch-to-chat-btn">
            <Keyboard size={14} />&nbsp;&nbsp;Switch to typing
          </button>
        </div>
      ) : (
        <>
          <div className="onboard-messages" data-testid="onboard-messages">
            {messages.map((m, i) => (
              <div key={i} className={`onboard-msg ${m.role}`} data-testid={`chat-msg-${m.role}`}>
                <div className="msg-who">{m.role === 'assistant' ? 'X Scale' : m.speaker}</div>
                <div className="msg-body">{m.text}</div>
              </div>
            ))}
            {streamText !== null && (
              <div className="onboard-msg assistant" data-testid="chat-msg-streaming">
                <div className="msg-who">X Scale</div>
                <div className="msg-body">{streamText}<span className="msg-cursor" /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {completeBanner}

          <div className="onboard-composer">
            <div className="composer-bar">
              <textarea
                ref={taRef}
                rows={1}
                value={draft}
                placeholder="Describe your project…"
                onChange={(e) => {
                  setDraft(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
                disabled={busy || dictation !== 'idle'}
                data-testid="chat-input"
              />
              <button
                className={`composer-btn mic ${dictation === 'recording' ? 'recording' : ''}`}
                onClick={toggleDictation}
                disabled={busy || dictation === 'transcribing'}
                aria-label={dictation === 'recording' ? 'Stop dictation' : 'Dictate'}
                data-testid="mic-btn"
              >
                {dictation === 'recording' ? <Square size={16} fill="currentColor" /> :
                  dictation === 'transcribing' ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
              </button>
              <button
                className="composer-btn send"
                onClick={handleSend}
                disabled={busy || dictation !== 'idle' || !draft.trim()}
                aria-label="Send"
                data-testid="send-btn"
              >
                <ArrowUp size={18} />
              </button>
            </div>
            <div className="composer-hint" data-testid="composer-hint">
              {error ? <span style={{ color: '#e08b66' }}>{error}</span> : hint()}
              {!busy && !completed && dictation === 'idle' && (
                <> · <button className="voice-exit" onClick={startVoiceMode} data-testid="switch-to-voice-btn"><AudioLines size={11} style={{ verticalAlign: '-1px' }} /> voice conversation</button></>
              )}
            </div>
          </div>
        </>
      )}

      {showShare && (
        <div className="onboard-modal-backdrop" onClick={() => setShowShare(false)} data-testid="share-modal">
          <div className="onboard-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Share with a teammate</h3>
            <p>Send this link to a teammate. They'll see the conversation so far and can add their perspective before the brief is submitted.</p>
            <div className="share-link-row">
              <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} data-testid="share-link-input" />
              <button className="button button-primary" onClick={copyLink} data-testid="copy-share-link-btn">
                {copied ? <><Check size={14} />&nbsp;Copied</> : <><Copy size={14} />&nbsp;Copy</>}
              </button>
            </div>
            <div className="modal-actions">
              <button className="button button-secondary" onClick={() => setShowShare(false)} data-testid="close-share-modal-btn">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
